import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {Link} from 'react-router-dom'
import {CSSTransition} from 'react-transition-group'
import {KTSVG, StorageHelpers, toAbsoluteUrl} from '../../../../_gori/helpers'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import UserService from '../../users/core/_requests'
import {TUTORIAL_CARDS} from '../core/_const'
import {useAuth} from '../../auth'
import clsx from 'clsx'
import {Button} from '../../../../_gori/partials/widgets'
import {ROUTES, TUTORIAL_CHECKLIST_TOGGLE_ID} from '../../../../_gori/constants'

interface TutorialChecklistProps {
  closeChecklist?: () => void
  closeRef?: React.MutableRefObject<(() => void) | null>
}

const TutorialChecklist: React.FC<TutorialChecklistProps> = ({
  closeChecklist: parentCloseChecklist,
  closeRef,
}) => {
  const intl = useIntl()
  const {loadingSwitch, currentUser} = useAuth()
  const {newCancelToken, isCancel} = useCancelToken()
  const [visibleTutorialCards, setVisibleTutorialCards] = useState(TUTORIAL_CARDS)
  const [tutorialCard, setTutorialCard] = useState<Record<string, boolean> | null>(null)
  const checklistRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [stepsCompleted, setStepsCompleted] = useState(1)
  // Calculate transform origin based on the VISIBLE button position
  // There are 2 ChecklistButton instances (one for mobile, one for desktop),
  // so we must find the one that is actually visible (non-zero bounding rect).
  const calculateTransformOrigin = useCallback(() => {
    const buttons = document.querySelectorAll(
      `#${TUTORIAL_CHECKLIST_TOGGLE_ID}`
    ) as NodeListOf<HTMLElement>

    // Check if the element is visible (has width and height)
    let button: HTMLElement | null = null
    buttons.forEach((btn) => {
      const rect = btn.getBoundingClientRect()
      if (rect.width > 0 && rect.height > 0) {
        button = btn
      }
    })

    if (button) {
      const buttonRect = (button as HTMLElement).getBoundingClientRect()

      // Calculate the button's center position
      const buttonCenterX = buttonRect.left + buttonRect.width / 2
      const buttonCenterY = buttonRect.top + buttonRect.height / 2

      // Modal is positioned at right: 0px, so we need to calculate from the right edge
      // The modal is 384px wide and positioned 40px from right (me-10 class = 2.5rem = 40px)
      const modalWidth = 384
      const modalRightOffset = 40
      const viewportWidth = window.innerWidth

      // Modal's left position in viewport
      const modalLeft = viewportWidth - modalWidth - modalRightOffset

      // Use the modal's actual viewport top if it is mounted, otherwise estimate
      // it from the header's current viewport position + CSS top offset.
      // This handles the case where the modal is not yet in the DOM (first render
      // after mount) but the header may have scrolled from its original position.
      const modalTopInViewport = checklistRef.current
        ? checklistRef.current.getBoundingClientRect().top
        : 0

      // Calculate button position relative to modal
      const originX = buttonCenterX - modalLeft
      const originY = buttonCenterY - modalTopInViewport

      return `${originX}px ${originY}px`
    }

    return 'top right' // fallback
  }, []) // stable – reads live values from refs at call-time

  const closeChecklist = useCallback(() => {
    // Recalculate transform origin at the moment of closing so the collapse
    // animation always points to the button's current viewport position.
    setTransformOrigin(calculateTransformOrigin())
    setIsOpen(false)
    // Wait for the animation to complete before calling the parent's closeChecklist
    setTimeout(() => {
      parentCloseChecklist && parentCloseChecklist()
    }, 600)
  }, [parentCloseChecklist, calculateTransformOrigin])
  useEffect(() => {
    if (closeRef) {
      closeRef.current = closeChecklist
    }
  }, [closeChecklist, closeRef])

  const [transformOrigin, setTransformOrigin] = useState('top right')

  const getTutorialCard = useCallback(async () => {
    try {
      const settings = await UserService.getTutorialCard({cancelToken: newCancelToken()})
      setTutorialCard(settings)
      // Recalculate transform origin with the button's current viewport position
      // (the API call may take time, during which the user may have scrolled).
      setTransformOrigin(calculateTransformOrigin())
      setIsOpen(true)
    } catch (error) {
      if (!isCancel(error)) {
        console.error('Error fetching tutorial card:', error)
      }
    }
  }, [newCancelToken, isCancel, calculateTransformOrigin])

  useEffect(() => {
    const updateTransformOrigin = () => {
      setTransformOrigin(calculateTransformOrigin())
    }

    updateTransformOrigin()
    window.addEventListener('resize', updateTransformOrigin)

    return () => {
      window.removeEventListener('resize', updateTransformOrigin)
    }
  }, [calculateTransformOrigin])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const toggleButton = document.querySelector('#tutorial-checklist-toggle')
      const isToggleButton = toggleButton?.contains(event.target as Node)
      if (isToggleButton) return

      if (checklistRef.current && !checklistRef.current.contains(event.target as Node)) {
        closeChecklist && closeChecklist()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [closeChecklist])

  const handleSkip = useCallback(
    async (cardKey: string) => {
      StorageHelpers.setItemLocalStorage('card' + cardKey, {hidden: true})
      window.dispatchEvent(new Event('storage'))

      try {
        // Mark the card as completed in the state
        setTutorialCard((prev) => {
          if (!prev) return null
          return {
            ...prev,
            [cardKey]: true,
          }
        })

        setVisibleTutorialCards((prev) =>
          prev.map((card) => (card.key === cardKey ? {...card, completed: true} : card))
        )

        await UserService.removeTutorialCard(
          {
            card_key: cardKey,
            is_show: false,
          },
          {cancelToken: newCancelToken()}
        )
      } catch (error: any) {
        if (isCancel(error)) return
        console.error('Error skipping tutorial card:', error)
      }
    },
    [isCancel, newCancelToken]
  )

  const filteredSteps = useMemo(() => {
    let firstIncompleteFound = false
    if (!tutorialCard) return []
    return visibleTutorialCards.map((card) => {
      // Check if the list item has been closed before
      const isCompletedCardClosed = currentUser?.user_profiles?.tutorial_cards?.some(
        (userCardOnboard: {card_key: string; is_show: any}) =>
          userCardOnboard.card_key === card.key && !userCardOnboard.is_show
      )
      // Check if list item is disabled in local storage
      const isCompletedInLocalStorage = isCompletedCardClosed
        ? true
        : StorageHelpers.getItemLocalStorage('card' + card.key)

      // Check if the list item is disabled in src/app/modules/orders/core/_const.ts
      const isCompletedBySetting = isCompletedInLocalStorage ? true : tutorialCard[card.key]

      const completed = !!isCompletedBySetting
      if (completed) StorageHelpers.setItemLocalStorage('card' + card.key, {hidden: true})
      window.dispatchEvent(new Event('storage'))
      const current = !completed && !firstIncompleteFound
      if (current) firstIncompleteFound = true

      return {
        ...card,
        completed,
        current,
      }
    })
  }, [currentUser?.user_profiles?.tutorial_cards, tutorialCard, visibleTutorialCards])

  useEffect(() => {
    let stepsCompletedCount = 1

    filteredSteps.forEach((item) => {
      if (item.completed) {
        stepsCompletedCount = stepsCompletedCount + 1
      }
    })

    setStepsCompleted(stepsCompletedCount)
  }, [filteredSteps])

  useEffect(() => {
    if (!loadingSwitch && currentUser) {
      getTutorialCard()
    }
  }, [getTutorialCard, loadingSwitch, currentUser])

  if (!currentUser) {
    return null
  }

  const ProgressText = () => {
    return (
      <div className='me-5'>
        <p className='text-white fs-7 mb-0 m-1 mt-2 poppins fw-bolder text-center progress-text'>
          {stepsCompleted === 6
            ? intl.formatMessage({id: 'ONBOARDING_DONE'})
            : `${stepsCompleted} ${intl.formatMessage({id: 'ONBOARDING_TOTAL_STEPS'})}`}
        </p>
      </div>
    )
  }

  const ProgressBar = () => {
    return (
      <div className='five-bar-progress d-flex flex-row flex-nowrap gap-1' style={{flex: 1}}>
        {[1, 2, 3, 4, 5].map((barIndex) => {
          let width: string
          if (barIndex < stepsCompleted) {
            width = '100%'
          } else if (barIndex === stepsCompleted) {
            width = '35%'
          } else {
            width = '0%'
          }

          return (
            <div className='progress w-100 mb-0 mt-2 ml-2' key={barIndex}>
              <div
                className='progress-bar bg-orange'
                role='progressbar'
                style={{
                  width,
                  transitionDelay: `${barIndex * 600}ms`,
                  transitionTimingFunction: `linear`,
                }}
                aria-valuenow={parseInt(width, 10) || 0}
                aria-valuemin={0}
                aria-valuemax={100}
              ></div>
            </div>
          )
        })}
      </div>
    )
  }

  const TutorialChecklistHeader = () => {
    return (
      <>
        {' '}
        {stepsCompleted === 1 && (
          <h5 className='mb-1 ms-6 m-0 text-white poppins fw-bolder fs-3 lh-base header-text'>
            {intl.formatMessage({id: 'FIRST_STEP_HEADER'})}
          </h5>
        )}
        {stepsCompleted > 1 && stepsCompleted < 6 && (
          <h5 className='mb-1 ms-6 m-0 text-white poppins fw-bolder fs-3 lh-base header-text'>
            {intl.formatMessage({id: 'MID_STEPS_HEADER'})}
          </h5>
        )}
        {stepsCompleted === 6 && (
          <h5 className='mb-1 ms-6 m-0 text-white poppins fw-bolder fs-3 lh-base header-text'>
            {intl.formatMessage({id: 'COMPLETED_STEPS_HEADER'})}
          </h5>
        )}
      </>
    )
  }

  const StepButton = ({linkTo, textId}) => (
    <Link to={linkTo} onClick={closeChecklist}>
      <button
        type='button'
        className={clsx(
          'card-button d-flex justify-content-center align-items-center gap-2 poppins fw-bolder lh-base text-align-center p-2',
          {
            'd-flex justify-content-center align-items-center p-2 gap-2 multi-link-actions-button':
              linkTo === `${ROUTES.ORDERS.CREATE}` || linkTo === `${ROUTES.BATCHES.BASE}`,
          }
        )}
      >
        {linkTo === `${ROUTES.ORDERS.CREATE}` && (
          <KTSVG path='/media/gori/onboard/welcome/order.svg' className='multi-actions-icon' />
        )}
        {linkTo === `${ROUTES.BATCHES.BASE}` && (
          <KTSVG path='/media/gori/onboard/welcome/batch.svg' className='multi-actions-icon' />
        )}
        {intl.formatMessage({id: textId})}
        <KTSVG path='/media/gori/arrows/arrow_right.svg' className='svg-icon-2' />
      </button>
    </Link>
  )

  const StepContent = ({card, index}) => (
    <div className='step-content pe-3'>
      <h6
        className={clsx('card-title overflow-hidden lh-base', {
          'text-decoration-line-through text-gray-600 mb-0': card.completed,
        })}
        style={!card.current ? {color: '#475569'} : undefined}
      >
        {`${index + 1}. ${intl.formatMessage({id: card.titleId})}`}
      </h6>
      {!card.completed && (
        <p
          className={clsx('card-text overflow-hidden lh-base')}
          style={!card.current ? {color: '#475569'} : undefined}
        >
          {intl.formatMessage({id: card.contentId})}
        </p>
      )}
    </div>
  )

  const StepActions = ({card}) => (
    <div
      className={clsx(
        'd-flex flex-column justify-content-center align-items-start step-actions-container',
        {
          'd-none': !card.current,
          'd-block': card.current,
        }
      )}
    >
      <StepButton linkTo={card.linkTo} textId='GO' />
      {card.canSkip && (
        <Button
          className='text-hover-dark border-0 bg-transparent p-0 fs-9'
          event={() => handleSkip(card.key)}
        >
          <span className='skip-button overflow-hidden lh-base'>
            {intl.formatMessage({id: 'SKIP'})}
            <KTSVG path='/media/gori/arrows/arrow_skip.svg' />
          </span>
        </Button>
      )}
    </div>
  )

  const MultiLinkActions = ({card}) => (
    <div
      className={clsx(
        'multi-link-actions d-flex mt-2 justify-content-start flex-row gap-3 w-100 step-actions-container',
        {
          'd-none': !card.current,
          'd-block': card.current,
        }
      )}
    >
      <StepButton linkTo={card.linkTo} textId='CREATE_FIRST_ORDER_SINGLE_BUTTON_TEXT' />
      <StepButton linkTo={card.linkTo2} textId='CREATE_FIRST_ORDER_BATCH_BUTTON_TEXT' />
    </div>
  )

  const CompletedChecklistOverlay = () =>
    stepsCompleted !== 6 ? null : (
      <div className='completed-checklist-overlay overflow-hidden blurred-bg d-flex justify-content-center align-items-center position-absolute top-0 left-0 z-index-2'>
        <div className='d-flex flex-column align-items-center gap-0'>
          <img
            className='mt-9 mb-3 overlay-gif'
            src={toAbsoluteUrl('/media/gori/onboard/welcome/complted-onboarding-checklist.gif')}
            alt='Complted GIF'
          />
          <p className='overlay-text fs-5 d-flex align-items-stretch text-center fw-bolder lh-base'>
            {intl.formatMessage({id: 'CHECKLIST_COMPLETED_MESSAGE_1'})}
            <br />
            {intl.formatMessage({id: 'CHECKLIST_COMPLETED_MESSAGE_2'})}
          </p>
          <div
            className={clsx(
              'd-flex flex-row mb-auto justify-content-center align-items-center gap-3'
            )}
          >
            <StepButton linkTo='/orders/create' textId='CREATE_FIRST_ORDER_SINGLE_BUTTON_TEXT' />
            <StepButton linkTo='/batches' textId='CREATE_FIRST_ORDER_BATCH_BUTTON_TEXT' />
          </div>
          <div className='pt-4 mb-7 overflow-hidden lh-base onboarding-checklist-close'>
            <span onClick={closeChecklist}>
              {intl.formatMessage({id: 'CHECKLIST_COMPLETED_MESSAGE_3'})}
            </span>
          </div>
        </div>
      </div>
    )

  return (
    <CSSTransition in={isOpen} timeout={600} classNames='pop' unmountOnExit>
      <div
        ref={checklistRef}
        className='topbar-dropdown-menu topbar-dropdown-menu--checklist onboarding-checklist'
        style={{
          right: 'calc(100% - 160px)',
          zIndex: 9999,
          isolation: 'isolate',
          transition: 'top 0.3s ease-in-out',
          transformOrigin,
        }}
      >
        <div className='card d-flex bg-white shadow-sm me-10 mw-384px' style={{width: '384px'}}>
          <div className='card-heading rounded-top py-4'>
            <div className='d-flex justify-content-between align-items-start'>
              <TutorialChecklistHeader />

              <div role='button' onClick={closeChecklist}>
                <KTSVG
                  path='/media/gori/onboard/welcome/minimize.svg'
                  className='svg-icon-2 text-white me-6'
                  svgClassName='text-white'
                />
              </div>
            </div>
            <div className='d-flex px-6 mt-3 align-items-center'>
              <ProgressText />
              <ProgressBar />
            </div>
          </div>

          <div className='position-relative'>
            <CompletedChecklistOverlay />

            <ol className='card-body d-flex flex-column p-0 list-unstyled m-0'>
              {filteredSteps.map((card, index) => (
                <li
                  key={card.key}
                  className={clsx('d-flex align-items-center', {
                    'flex-row current-card-padding': card.current,
                    'flex-row border-bottom': index !== filteredSteps.length - 1,
                    'flex-column': index === 2 && !card.completed,
                    'completed-card': card.completed,
                    'rounded-bottom': index === filteredSteps.length - 1,
                  })}
                  style={{backgroundColor: card.current ? '#FFFAE8' : '#FFF'}}
                >
                  <StepContent card={card} index={index} />
                  {card.completed && (
                    <div className='card-completed-text position-relative overflow-hidden lh-base gap-2 fw-bolder'>
                      <span>{intl.formatMessage({id: 'COMPLETED'})}!</span>
                      <KTSVG
                        className='completed-icon'
                        path='media/gori/onboard/welcome/completed-check.svg'
                      />
                    </div>
                  )}
                  {card.key === 'order' && !card.completed && card.linkTo2 ? (
                    <MultiLinkActions card={card} />
                  ) : (
                    <StepActions card={card} />
                  )}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </CSSTransition>
  )
}

export {TutorialChecklist}
